import {
  formatCompanionDate,
  formatCompanionMoney,
} from '@/components/companion/feed/companion-formatters';
import type { CompanionEvidence } from '@fluenceur/types';
import type { TFunction } from 'i18next';

/**
 * What the companion saw, in the reader's words.
 *
 * Only fields the detector actually froze at publication time are turned into a
 * line: no derived figure, no rounding of something the evidence does not
 * carry. A brand that reads "18 creators" here must be able to count eighteen.
 */

export const companionEvidenceLines = (
  evidence: CompanionEvidence,
  t: TFunction,
  language: string
): string[] => {
  switch (evidence.kind) {
    case 'brandmatch_batch': {
      const lines = [
        t('companion.why.brandmatchBatch.creators', { count: evidence.creatorCount }),
        t('companion.why.brandmatchBatch.delivered', {
          date: formatCompanionDate(evidence.deliveredAt, language),
        }),
      ];
      if (evidence.cadenceDays !== null) {
        lines.push(t('companion.why.brandmatchBatch.cadence', { count: evidence.cadenceDays }));
      }
      return lines;
    }
    case 'rights_expiring': {
      const lines = [
        t('companion.why.rightsExpiring.daysRemaining', { count: evidence.daysRemaining }),
        t('companion.why.rightsExpiring.endsAt', {
          date: formatCompanionDate(evidence.endsAt, language),
        }),
        t('companion.why.rightsExpiring.terms', {
          amount: formatCompanionMoney(
            { currencyCode: evidence.terms.currencyCode, amountCents: evidence.terms.amountCents },
            language
          ),
          count: evidence.terms.durationMonths,
        }),
      ];
      if (evidence.attestedOffPlatform) {
        lines.push(t('companion.why.rightsExpiring.attested'));
      }
      // The same wording as the card's own line, deliberately: the panel that
      // explains a card must not describe it in different words — including
      // when what it has to say is that there is nothing to compare.
      lines.push(
        evidence.performance
          ? t('companion.why.rightsExpiring.performance', {
              value: evidence.performance.value.toFixed(1),
              median: evidence.performance.medianOver90d.toFixed(1),
              count: evidence.performance.comparableCount,
            })
          : t('companion.why.rightsExpiring.performanceUnavailable')
      );
      return lines;
    }
    case 'payout_pending': {
      // The same wording as the card's own lines, deliberately: the panel that
      // explains a card must not describe it in different words — including
      // keeping every currency on its own line rather than adding them.
      const lines = evidence.amountsByCurrency.map((entry) =>
        t('companion.cards.payoutPending.amount', {
          amount: formatCompanionMoney(
            { currencyCode: entry.currencyCode, amountCents: entry.totalAmountCents },
            language
          ),
          count: entry.commissionCount,
        })
      );
      lines.push(
        t('companion.why.payoutPending.affiliates', { count: evidence.affiliateCount }),
        t('companion.why.payoutPending.commissions', { count: evidence.commissionCount }),
        t('companion.why.payoutPending.oldest', {
          date: formatCompanionDate(evidence.oldestPendingAt, language),
        })
      );
      if (evidence.excludedAffiliates.length > 0) {
        lines.push(
          t('companion.cards.payoutPending.excluded', {
            count: evidence.excludedAffiliates.length,
          })
        );
      }
      if (!evidence.brandPayoutConfigured) {
        lines.push(t('companion.cards.payoutPending.billingMissing'));
      }
      return lines;
    }
    case 'mention_notable': {
      const lines = [
        t('companion.why.mentionNotable.published', {
          date: formatCompanionDate(evidence.publishedAt, language),
        }),
        t('companion.why.mentionNotable.platform', { platform: evidence.platform }),
      ];
      // The same wording as the card's own lines, deliberately: the panel that
      // explains a card must not describe it in different words.
      if (evidence.qualifiedBy.includes('trend')) {
        lines.push(
          evidence.trendRatio === null
            ? t('companion.cards.mentionNotable.signal.trend')
            : t('companion.cards.mentionNotable.signal.trendWithValue', {
                ratio: evidence.trendRatio.toFixed(1),
              })
        );
      }
      if (evidence.qualifiedBy.includes('score')) {
        lines.push(
          evidence.matchScore === null
            ? t('companion.cards.mentionNotable.signal.score')
            : t('companion.cards.mentionNotable.signal.scoreWithValue', {
                score: evidence.matchScore,
              })
        );
      }
      if (evidence.qualifiedBy.includes('frequency')) {
        lines.push(
          t('companion.cards.mentionNotable.signal.frequency', {
            count: evidence.mentionsLast30d,
          })
        );
      }
      return lines;
    }
    case 'affiliate_silent': {
      const lines = [
        t('companion.why.affiliateSilent.daysSilent', { count: evidence.daysSilent }),
        t('companion.why.affiliateSilent.orders', { count: evidence.ordersInWindow }),
      ];
      if (evidence.observedRevenue) {
        lines.push(
          t('companion.why.affiliateSilent.revenue', {
            amount: formatCompanionMoney(evidence.observedRevenue, language),
          })
        );
      }
      return lines;
    }
    case 'gift_unclaimed': {
      // The same facts as the card's own lines, deliberately: the panel that
      // explains a card must not describe it in different words.
      const lines = [
        t('companion.why.giftUnclaimed.daysRemaining', { count: evidence.daysRemaining }),
        evidence.productTitle
          ? t('companion.why.giftUnclaimed.product', { title: evidence.productTitle })
          : t('companion.why.giftUnclaimed.productChoice', { count: evidence.productCount }),
        t('companion.why.giftUnclaimed.offeredAt', {
          date: formatCompanionDate(evidence.offeredAt, language),
        }),
        t('companion.why.giftUnclaimed.expiresAt', {
          date: formatCompanionDate(evidence.expiresAt, language),
        }),
      ];
      if (evidence.giftValue) {
        lines.push(
          t('companion.why.giftUnclaimed.value', {
            amount: formatCompanionMoney(evidence.giftValue, language),
          })
        );
      }
      return lines;
    }
    default:
      return [];
  }
};
